import { useEffect, useState } from 'react';
import { faCheck, faRecycle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { useNavigate } from 'react-router-dom';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory, {
  PaginationTotalStandalone,
  PaginationProvider,
  PaginationListStandalone
} from 'react-bootstrap-table2-paginator';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Alert, Spinner } from 'react-bootstrap';
import Badge from 'components/base/Badge';
import FilterTab from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import FeatherIcon from 'feather-icons-react';
import { CustomSortCaret } from 'helpers/ui-utils';
import { getQbInvoice, isQbConnected, createInvoice } from 'api/api';
import siteConfig from '../../config/site.config';

const QbInvoice = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [error, setError] = useState([]);
  const [total, setTotal] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState('asc');
  const [columnName, setColumnName] = useState('id');
  const [sortString, setSortString] = useState('id:desc');
  const [qbConnect, setQbConnect] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      await connectQb();
      await loadData(currentPage, pageSize, 'id:desc');
    };

    fetchData();
  }, [isLoaded]);

  const loadData = async (pageNr, pageSize, sortBy) => {
    setError([]);
    setLoading(true);

    try {
      const response = await getQbInvoice(pageNr, pageSize, sortBy);
      console.log(response);
      setData(response.data.data.invoicesList);
      setTotal(response.data.data.total);
    } catch (err) {
      try {
        if (err.response?.status === 401) {
          navigate('/sign-in');
          return; // Exit early after navigation
        }
        setError(err.response?.data?.errors || []);
      } catch (ex) {
        setError(['Please try again later...']);
      }
    } finally {
      setLoading(false);
    }
  };

  const connectQb = async () => {
    setError([]);
    setLoading(true);

    try {
      const response = await isQbConnected();
      if (response.data.data === 'Connected') setQbConnect(true);
      else setQbConnect(false);
    } catch (err) {
      setLoading(false);
      try {
        if (err.response?.status === 401) {
          navigate('/sign-in');
          return;
        }

        const errors = err.response?.data?.errors;

        if (
          errors === 'QB Disconnected' ||
          (Array.isArray(errors) && errors.includes('QB Disconnected'))
        ) {
          setQbConnect(false);
        } else {
          setError(errors || []);
        }
      } catch (ex) {
        setError(['Please try again later...']);
      }
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const CustomSortCaretUpdatedByOrder = (order, dataField) => {
    if (columnName === 'id')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            ▲
            <br />▼
          </div>
        </span>
      );
    else if (sortColumn === 'asc')
      return (
        <span className="custom-sort-cart">
          <div style={{ display: 'inline-block' }}>
            <div style={{ display: 'inline-block' }}> ▲ </div>
            <br />
            <div style={{ display: 'inline-block', visibility: 'collapse' }}>
              ▼
            </div>
          </div>
        </span>
      );
    else if (sortColumn === 'desc')
      return <span className="custom-sort-cart">▼</span>;
    return null;
  };

  const columns = [
    {
      dataField: 'client.name',
      text: 'NAME',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'name' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        fontWeight: 'bold',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'client.address',
      text: 'ADDRESS',
      headerStyle: { width: '15%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'address'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },

    {
      dataField: 'client.city',
      text: 'CITY',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'city' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'client.state',
      text: 'STATE',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'state'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'client.zip',
      text: 'ZIP',
      headerStyle: { width: '10%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'zip' ? CustomSortCaretUpdatedByOrder : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      }
    },
    {
      dataField: 'invoice_id',
      text: 'INVOICE ID',
      headerStyle: { width: '8%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'invoice_id'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100',
        fontWeight: 'bold'
      }
    },

    {
      dataField: 'price',
      text: 'AMOUNT',
      headerStyle: { width: '8%', fontSize: '13px' },
      sort: false,
      sortCaret:
        columnName === 'phone'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100',
        fontWeight: 'bold'
      },
      formatter: (cell, row) => {
        const formattedPrice =
          row.price === '0' ? '' : `$${parseFloat(row.price).toFixed(2)}`;
        return formattedPrice;
      }
    },
    {
      dataField: 'status',
      text: 'STATUS',
      sort: false,
      sortCaret:
        columnName === 'status'
          ? CustomSortCaretUpdatedByOrder
          : CustomSortCaret,
      headerStyle: { width: '10%', fontSize: '13px' },
      style: {
        fontSize: '12px',
        color: 'phoenix-gray-1100'
      },
      formatter: (cell, row) => {
        if (row.status.name === 'Active') {
          return (
            <Badge
              bg={'success'}
              variant="phoenix"
              iconPosition="end"
              className="fs-10"
              icon={<FeatherIcon icon={'check'} size={12} className="ms-1" />}
            >
              {row.status.name}
            </Badge>
          );
        } else {
          return (
            <Badge
              bg={'danger'}
              variant="phoenix"
              iconPosition="end"
              className="fs-10"
              icon={<FeatherIcon icon={'info'} size={12} className="ms-1" />}
            >
              {row.status.name}
            </Badge>
          );
        }
      }
    },

    {
      dataField: 'actions',
      width: '15%',
      formatter: (cell, row) =>
        row.invoice_id !== null ? (
          ''
        ) : (
          <div>
            <Button
              variant="primary"
              onClick={() => handleCreateInvoice(row)}
              disabled={!qbConnect}
            >
              Create an invoice
            </Button>
          </div>
        )
    }
  ];

  const handleAllClick = () => {
    setCurrentPage(1);
    loadData(1, 10, 'id:desc');
  };

  const tabItems = [
    {
      label: 'All',
      value: 'all',
      count: total,
      onClick: handleAllClick
    }
  ];

  const table = useAdvanceTable({
    data: data
  });

  const handleAuthQb = async e => {
    e.preventDefault();
    handleOpenBrowser();
    // setError([]);
    // try {
    //   const response = await authQb();
    //   console.log(response);
    //   if (response.status === 200) {
    //     handleOpenBrowser();
    //   }
    // } catch (err) {
    //   handleOpenBrowser();
    //   try {
    //     if (err.response?.status === 401) {
    //       navigate('/sign-in');
    //       return;
    //     }
    //     const errors = err.response?.data?.errors;
    //     setError(errors || []);
    //   } catch (ex) {
    //     setError(['Please try again later...']);
    //   }
    // }
  };

  const handleOpenBrowser = () => {
    const screenWidth = window.screen.width;
    const screenHeight = window.screen.height;

    const width = 800;
    const height = 600;

    const left = (screenWidth - width) / 2;
    const top = (screenHeight - height) / 2;

    const redirectUrl = siteConfig.qbUrl;

    window.open(
      redirectUrl,
      '_blank',
      `width=${width},height=${height},top=${top},left=${left},toolbar=no,menubar=no,scrollbars=yes,resizable=yes,status=no`
    );
  };

  const handleCreateInvoice = async row => {
    setError([]);
    setLoading(true);
    try {
      const response = await createInvoice(row.id);
      if (response.status === 200) setIsLoaded(!isLoaded);
    } catch (err) {
      try {
        if (err.response?.status === 401) {
          navigate('/sign-in');
          return; // Exit early after navigation
        }
        const errorData = err.response?.data?.errors?.Fault?.Error || [];
        const errorMessages = errorData.map(
          e => `${e.Message} - Client: ${row.client.name}`
        );
        setError(
          errorMessages.length > 0
            ? errorMessages
            : [`An unknown error occurred - Client: ${row.client.name}`]
        );
      } catch (ex) {
        setError(['Please try again later...']);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setIsLoaded(!isLoaded);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, data }
  ) => {
    switch (type) {
      case 'pagination':
        pagination(page, sizePerPage);
        break;
      case 'sort':
        sort(sortField, sortOrder, data);
        break;
      default:
        break;
    }
  };

  const pagination = (page, sizePerPage) => {
    setCurrentPage(page);
    setPageSize(sizePerPage);
    loadData(page, sizePerPage, sortString);
  };

  const sort = sortField => {
    setCurrentPage(1);
    if (sortColumn === 'desc') {
      setSortColumn('asc');
    } else if (sortColumn === 'asc') {
      setSortColumn('desc');
    }
    switch (sortField) {
      case 'first_name':
        setColumnName('first_name');
        setSortString(`first_name:${sortColumn}`);
        loadData(1, 10, `first_name:${sortColumn}`);
        break;
      case 'last_name':
        setColumnName('last_name');
        setSortString(`last_name:${sortColumn}`);
        loadData(1, 10, `last_name:${sortColumn}`);
        break;
      case 'group':
        setColumnName('group');
        setSortString(`user_group.name:${sortColumn}`);
        loadData(1, 10, `user_group.name:${sortColumn}`);
        break;
      case 'status':
        setColumnName('status');
        setSortString(`status.name:${sortColumn}`);
        loadData(1, 10, `status.name:${sortColumn}`);
        break;
      default:
        break;
    }
  };

  return (
    <div>
      <div className="mb-9">
        <div>
          <h2 className="mb-2">QuickBooks Invoices</h2>
          <h5 className="text-700 fw-semi-bold">
            {`If the 'Connect to QuickBooks' button is enabled, click it to be redirected to the QuickBooks page, complete the connection process, return here, and click the refresh button to load the invoice list and continue the invoice creation process.`}
          </h5>
        </div>
        <FilterTab tabItems={tabItems} className="mb-2" />

        <AdvanceTableProvider {...table}>
          <div className="mb-4">
            <div className="d-flex flex-wrap gap-3">
              <div className="scrollbar overflow-hidden-y"></div>
              <div className="ms-xxl-auto">
                <Button
                  variant="primary"
                  className="me-2"
                  onClick={e => handleRefresh(e)}
                >
                  <FontAwesomeIcon icon={faRecycle} className="me-2" />
                  Refresh
                </Button>
                <Button
                  variant={qbConnect === true ? 'success' : 'danger'}
                  onClick={e => handleAuthQb(e)}
                  disabled={qbConnect}
                >
                  <FontAwesomeIcon
                    icon={qbConnect === true ? faCheck : ''}
                    className="me-2"
                  />
                  {qbConnect == true
                    ? `QuickBooks connected`
                    : `Connect to QuickBooks`}
                </Button>
              </div>
            </div>
          </div>

          <div>
            `
            {Object.entries(error).length > 0 &&
              error.map((e, index) => (
                <Alert variant="soft-danger" key={index}>
                  {e}
                </Alert>
              ))}
          </div>

          <div className="bg-white border-top border-bottom border-200 position-relative top-1">
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '200px' }}
              >
                <Spinner animation="border" role="status"></Spinner>
              </div>
            ) : (
              <PaginationProvider
                pagination={paginationFactory({
                  custom: true,
                  page: currentPage,
                  sizePerPage: pageSize,
                  totalSize: total,
                  sizePerPageList: [
                    {
                      text: '10',
                      value: 10
                    }
                  ]
                })}
              >
                {({ paginationProps, paginationTableProps }) => (
                  <div>
                    <BootstrapTable
                      remote
                      keyField="id"
                      data={data.length === 0 ? [] : data}
                      columns={columns}
                      bordered={false}
                      onTableChange={handleTableChange}
                      {...paginationTableProps}
                    />
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '-10px',
                          fontSize: '13px'
                        }}
                      >
                        <PaginationTotalStandalone {...paginationProps} />
                        <PaginationListStandalone {...paginationProps} />
                      </div>
                    </div>
                  </div>
                )}
              </PaginationProvider>
            )}
          </div>
        </AdvanceTableProvider>
      </div>
    </div>
  );
};

export default QbInvoice;
